import { useState } from 'react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { CurrencyInput } from 'components/ui/currency-input';
import { Label } from 'components/ui/label';

import { Typography } from 'components/ui/typography';
import { formatPrice } from 'utils/formatters';
import { toast } from 'sonner';

import { useBilling, useSetupDrawdown } from '../hooks/useBilling';
import { useAppDispatch, useAppSelector } from 'app/store';
import { fetchUserInfo, selectUserInfo } from 'app/store/user';
import { canWrite } from 'app/access';
import { PaymentDialog } from 'pages/Billing/components/PaymentDialog';
import { useAuth } from 'providers/AuthProvider';

export function DrawdownSettingsCard() {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const userInfo = useAppSelector(selectUserInfo);
  const canEditBilling = canWrite(userInfo, 'billing');
  const { drawdown, isLoading, error: fetchError, refetch } = useBilling();
  const { setupDrawdown } = useSetupDrawdown();
  const [isEditing, setIsEditing] = useState(false);
  const [targetDollars, setTargetDollars] = useState('');
  const [minDollars, setMinDollars] = useState('');
  const [chargeAutomatically, setChargeAutomatically] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="h-48 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card>
        <CardContent className="p-6">
          <Typography variant="small" as="p">
            Unable to load billing information. Please refresh the page to try again.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!drawdown?.enabled) {
    return null;
  }

  const { config, balance } = drawdown;

  if (!config || !balance) {
    return null;
  }

  const handlePaymentMethodAdded = () => {
    if (user) {
      dispatch(fetchUserInfo(user.uid, user));
    }
    void refetch();
  };

  const handleEdit = () => {
    if (!canEditBilling) {
      return;
    }

    setTargetDollars((config.targetBalanceSubunits / 100).toFixed(2));
    setMinDollars((config.minBalanceSubunits / 100).toFixed(2));
    setChargeAutomatically(config.chargeAutomatically);
    setError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
  };

  const validateInputs = (): string | null => {
    const target = parseFloat(targetDollars);
    const min = parseFloat(minDollars);

    if (!targetDollars || isNaN(target)) {
      return 'Target balance is required';
    }

    if (!minDollars || isNaN(min)) {
      return 'Minimum threshold is required';
    }

    if (target < 0) {
      return 'Target balance must be non-negative';
    }

    if (min < 0) {
      return 'Minimum threshold must be non-negative';
    }

    if (min > target) {
      return 'Minimum threshold must be less than or equal to target balance';
    }

    return null;
  };

  const handleSave = async () => {
    if (!canEditBilling) {
      return;
    }

    setError(null);

    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    const target = parseFloat(targetDollars);
    const min = parseFloat(minDollars);

    setIsSaving(true);
    try {
      await setupDrawdown({
        targetBalanceSubunits: Math.round(target * 100),
        minBalanceSubunits: Math.round(min * 100),
        chargeAutomatically,
      });
      await refetch();
      setIsEditing(false);
      toast.success('Drawdown settings updated. Changes will apply on the next top-up cycle.', {
        id: 'drawdown-settings-updated',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update drawdown settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Current Balance</CardTitle>
          </div>
          <Badge variant="success">Active</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Typography variant="h2" as="p">
            {formatPrice(balance.amountSubunits / 100, balance.currencyCode)}
          </Typography>
        </div>

        <div className="border-t pt-6">
          <div className="mb-4 flex items-center justify-between">
            <Typography variant="h4" as="h3">
              Drawdown Settings
            </Typography>
            {!isEditing && canEditBilling && (
              <div className="flex items-center gap-2">
                <PaymentDialog onSubmit={handlePaymentMethodAdded} />
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              </div>
            )}
          </div>
          <Typography variant="small" as="p" className="mb-4">
            An invoice is automatically created when your balance falls below the threshold. With
            auto-pay enabled, it&apos;s charged immediately.
          </Typography>

          {!isEditing ? (
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label>Target Balance</Label>
                <Typography variant="mono" as="p" className="tabular-nums">
                  {formatPrice(config.targetBalanceSubunits / 100, config.currency)}
                </Typography>
              </div>
              <div className="space-y-1">
                <Label>Min Threshold</Label>
                <Typography variant="mono" as="p" className="tabular-nums">
                  {formatPrice(config.minBalanceSubunits / 100, config.currency)}
                </Typography>
              </div>
              <div className="space-y-1">
                <Label>Currency</Label>
                <Typography variant="mono" as="p">
                  {config.currency}
                </Typography>
              </div>
              <div className="space-y-1">
                <Label>Auto-pay Invoices</Label>
                <Typography variant="body" as="p">
                  {config.chargeAutomatically ? 'Yes' : 'No'}
                </Typography>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-target-balance">Target Balance</Label>
                <CurrencyInput
                  id="edit-target-balance"
                  value={targetDollars}
                  onChange={setTargetDollars}
                  placeholder="10,000.00"
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-min-threshold">Minimum Threshold</Label>
                <CurrencyInput
                  id="edit-min-threshold"
                  value={minDollars}
                  onChange={setMinDollars}
                  placeholder="5,000.00"
                  disabled={isSaving}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-charge-automatically"
                  checked={chargeAutomatically}
                  onCheckedChange={(checked) => setChargeAutomatically(checked === true)}
                  disabled={isSaving}
                />
                <Label htmlFor="edit-charge-automatically">Automatically pay invoices</Label>
              </div>

              {error && (
                <Typography variant="small" as="p" className="text-destructive">
                  {error}
                </Typography>
              )}

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
